import type { ErrorDefinition, FriendlyError } from './types';

/**
 * Waitlist signup error definitions.
 * Maps backend codes from `POST /api/waitlist` and client-side fallback
 * codes to user-friendly messages. Mirrors the `subscriptionErrors.ts` /
 * `landingErrors.ts` per-domain pattern.
 */
export const WAITLIST_ERRORS: Record<string, ErrorDefinition> = {
  // ---------------------------------------------------------
  // Backend codes (UPPERCASE, sent in error.code)
  // ---------------------------------------------------------
  INVALID_EMAIL: {
    title: 'Check your email',
    message: "That email address doesn't look valid.",
    action: 'Double-check for typos and try again.',
    retryable: true,
    category: 'validation',
  },

  ALREADY_SUBSCRIBED: {
    title: "You're already on the list",
    message: "That email is already signed up — we'll be in touch.",
    action: 'No action needed.',
    retryable: false,
    category: 'account',
  },

  RATE_LIMIT_EXCEEDED: {
    title: 'Slow down a moment',
    message: 'Too many signups from this connection in a short time.',
    action: 'Wait a minute and try again.',
    retryable: true,
    retryAfterSeconds: 60,
    category: 'rate_limit',
  },

  VALIDATION_ERROR: {
    title: 'Check your email',
    message: "That email address didn't pass validation.",
    action: 'Double-check for typos and try again.',
    retryable: true,
    category: 'validation',
  },

  INTERNAL_ERROR: {
    title: 'Something went wrong',
    message:
      "We had trouble saving your email. We've been notified and are looking into it.",
    action: 'Please try again in a moment.',
    retryable: true,
    retryAfterSeconds: 5,
    category: 'server',
  },

  // ---------------------------------------------------------
  // Client fallback codes (lowercase, when error.code is missing
  // from the backend response or the request never reached the server).
  // ---------------------------------------------------------
  waitlist_join_failed: {
    title: "Couldn't join the waitlist",
    message: 'We had trouble reaching our server.',
    action: 'Check your connection and try again.',
    retryable: true,
    category: 'network',
  },

  network_offline: {
    title: "You're offline",
    message: 'Joining the waitlist needs an internet connection.',
    action: 'Reconnect and try again.',
    retryable: true,
    category: 'network',
  },
};

/**
 * Get a friendly error for a waitlist-related error code.
 * Returns the offline entry when the device is offline, regardless of input code.
 */
export function getWaitlistError(code: string): FriendlyError {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { ...WAITLIST_ERRORS.network_offline, code: 'network_offline' };
  }

  const errorDef = WAITLIST_ERRORS[code] ?? {
    title: 'Something went wrong',
    message:
      "An unexpected error occurred. We've been notified and are looking into it.",
    action: 'Please try again.',
    retryable: true,
    category: 'unknown' as const,
  };

  return { ...errorDef, code };
}
